import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import { HseDocumentSelector } from "../../utils/moduleSelectors/hseDocumentSelector";
import { PMField } from "../../utils/PMField";
import { fetchHseEvents } from "../../../api/hse/events";
import { HseFieldInfoLabel } from "./HseFieldInfoLabel";
import { HseDialogFooter } from "./HseDialogFooter";
import {
  HSE_TILTAK_CATEGORIES,
  HSE_TILTAK_PRIORITIES,
  HSE_TILTAK_STATUSES,
  HSE_TILTAK_TYPES,
  emptyTiltakForm,
  toHseActionPayload,
} from "./hseTiltakUtils";

const fieldClass =
  "mt-2 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20";

export function HseTiltakForm({
  initialValues,
  onSubmit,
  onCancel,
  loading = false,
  submitLabel,
}) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchHseEvents({ page: 1, per_page: 50, sort_by: "event_date", sort_order: "desc" })
      .then((response) => {
        if (!cancelled) setEvents(response?.data || []);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues || emptyTiltakForm(),
    validate: (values) => {
      const errors = {};
      if (!values.title?.trim()) errors.title = t("hse.tiltak.error_title");
      if (!values.deadline) errors.deadline = t("hse.tiltak.error_deadline");
      if (!values.responsible_user_id) errors.responsible_user_id = t("hse.tiltak.error_assignee");
      if (values.action_type === "linked_document" && !values.hse_document_id) {
        errors.hse_document_id = t("hse.tiltak.error_document");
      }
      return errors;
    },
    onSubmit: async (values, helpers) => {
      try {
        await onSubmit(toHseActionPayload(values));
        helpers.setSubmitting(false);
      } catch (err) {
        helpers.setSubmitting(false);
        helpers.setErrors({ submit: err.message });
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-5">
      <div>
        <HseFieldInfoLabel
          label="hse.tiltak.field.title"
          infoTitle="hse.tiltak.info.title.title"
          infoBody="hse.tiltak.info.title.body"
        />
        <PMField
          name="title"
          value={formik.values.title}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.title && formik.errors.title}
          required
        />
      </div>

      <div>
        <HseFieldInfoLabel
          label="hse.tiltak.field.description"
          infoTitle="hse.tiltak.info.description.title"
          infoBody="hse.tiltak.info.description.body"
        />
        <PMField
          multiline
          name="description"
          rows={4}
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <HseFieldInfoLabel
            label="hse.tiltak.field.action_type"
            infoTitle="hse.tiltak.info.action_type.title"
            infoBody="hse.tiltak.info.action_type.body"
          />
          <select
            name="action_type"
            value={formik.values.action_type}
            onChange={formik.handleChange}
            className={fieldClass}
          >
            {HSE_TILTAK_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`hse.tiltak.type.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <HseFieldInfoLabel
            label="hse.tiltak.field.category"
            infoTitle="hse.tiltak.info.category.title"
            infoBody="hse.tiltak.info.category.body"
          />
          <select
            name="category"
            value={formik.values.category}
            onChange={formik.handleChange}
            className={fieldClass}
          >
            {HSE_TILTAK_CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {t(`hse.tiltak.category.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <HseFieldInfoLabel
            label="hse.tiltak.field.priority"
            infoTitle="hse.tiltak.info.priority.title"
            infoBody="hse.tiltak.info.priority.body"
          />
          <select
            name="priority"
            value={formik.values.priority}
            onChange={formik.handleChange}
            className={fieldClass}
          >
            {HSE_TILTAK_PRIORITIES.map((value) => (
              <option key={value} value={value}>
                {t(`hse.tiltak.priority.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <HseFieldInfoLabel
            label="hse.tiltak.field.status"
            infoTitle="hse.tiltak.info.status.title"
            infoBody="hse.tiltak.info.status.body"
          />
          <select
            name="status"
            value={formik.values.status}
            onChange={formik.handleChange}
            className={fieldClass}
          >
            {HSE_TILTAK_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`hse.tiltak.status.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <HseFieldInfoLabel
            label="hse.tiltak.field.deadline"
            infoTitle="hse.tiltak.info.deadline.title"
            infoBody="hse.tiltak.info.deadline.body"
          />
          <PMField
            type="date"
            name="deadline"
            value={formik.values.deadline}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.deadline && formik.errors.deadline}
            required
          />
        </div>

        <div>
          <HseFieldInfoLabel
            label="hse.tiltak.field.assignee"
            infoTitle="hse.tiltak.info.assignee.title"
            infoBody="hse.tiltak.info.assignee.body"
          />
          <div className="mt-2">
            <EmployeeSelector
              name="responsible_user_id"
              label="hse.tiltak.field.assignee"
              noLabel
              values={formik.values}
              setFieldValue={formik.setFieldValue}
              errors={formik.errors}
              selected={
                formik.values.responsible_user
                  ? {
                      id: formik.values.responsible_user.id,
                      displayTitle:
                        formik.values.responsible_user.displayTitle ||
                        formik.values.responsible_user.name,
                    }
                  : formik.values.responsible_user_id
                    ? { id: formik.values.responsible_user_id }
                    : null
              }
            />
          </div>
          {formik.touched.responsible_user_id && formik.errors.responsible_user_id ? (
            <p className="mt-1 text-xs text-red-600">{formik.errors.responsible_user_id}</p>
          ) : null}
        </div>
      </div>

      <div>
        <HseFieldInfoLabel
          label="hse.tiltak.field.event"
          infoTitle="hse.tiltak.info.event.title"
          infoBody="hse.tiltak.info.event.body"
        />
        <select
          name="hse_event_id"
          value={formik.values.hse_event_id || ""}
          onChange={formik.handleChange}
          className={fieldClass}
        >
          <option value="">{t("hse.tiltak.event_none")}</option>
          {events.map((event) => (
            <option key={event.id} value={event.id}>
              {event.title || event.display_title || `#${event.id}`}
            </option>
          ))}
        </select>
      </div>

      <div>
        <HseFieldInfoLabel
          label="hse.tiltak.field.document"
          infoTitle="hse.tiltak.info.document.title"
          infoBody="hse.tiltak.info.document.body"
        />
        <div className="mt-2">
          <HseDocumentSelector
            name="hse_document_id"
            label="hse.tiltak.field.document"
            noLabel
            values={formik.values}
            setFieldValue={formik.setFieldValue}
            errors={formik.errors}
            selected={formik.values.hse_document || null}
          />
        </div>
      </div>

      {formik.errors.submit ? (
        <p className="text-sm text-red-600">{formik.errors.submit}</p>
      ) : null}

      <div className="border-t border-slate-200 pt-5">
        <HseDialogFooter
          onCancel={onCancel}
          cancelLabel={t("hse.tiltak.cancel")}
          submitLabel={submitLabel || t("hse.tiltak.save")}
          submitting={loading || formik.isSubmitting}
        />
      </div>
    </form>
  );
}
